// Persistent banner above the hero when the snapshot is stale, the API
// 503'd, or the device is offline. Same component, three modes.
// role=status + aria-live=polite so screen readers announce state changes
// without interrupting.

export type BannerState =
  | { kind: 'hidden' }
  | { kind: 'stale'; age_minutes: number }
  | { kind: 'offline' }
  | { kind: 'error'; message: string; retry_after_s?: number };

export function renderStaleBanner(state: BannerState): string {
  if (state.kind === 'hidden') return '';

  const content = (() => {
    switch (state.kind) {
      case 'stale':
        return {
          title: `Data from ${state.age_minutes} min ago`,
          body: 'A fresher snapshot is on the way — refresh to check now.',
        };
      case 'offline':
        return {
          title: 'You appear to be offline',
          body: 'Showing the last successful fetch. Will retry automatically when you reconnect.',
        };
      case 'error':
        return {
          title: 'Could not fetch the latest data',
          body: state.retry_after_s
            ? `Trying again in ${state.retry_after_s}s. ${state.message}`
            : state.message,
        };
    }
  })();

  const showRetry = state.kind !== 'offline';

  return `
    <div class="stale-banner stale-banner--${state.kind}" role="status" aria-live="polite">
      <div class="stale-banner__copy">
        <div class="stale-banner__title">${escapeHtml(content.title)}</div>
        <div class="stale-banner__body">${escapeHtml(content.body)}</div>
      </div>
      ${showRetry ? `<button type="button" class="stale-banner__retry" data-stale-retry>Retry</button>` : ''}
    </div>
  `;
}

function escapeHtml(s: string): string {
  return s.replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!),
  );
}
